import React, { MouseEvent, useCallback } from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import amplitudeLog from '@client/amplitude';
import { AMP_DESIGN_STOREY_DELETE } from '@shared/constants';
import { Storey } from '@shared/models/design/storey.model';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import { Tooltip } from '@ui/Tooltip';
import { IconButton } from '@ui/buttons';
import { usePanelStoreys, useRemoveStorey } from '../hooks/storeys.hooks';
import { isDesignSnapshotSupersededError } from '../helpers/design-snapshot-persist.helpers';
import { HoverDeleteButton } from '../components/HoverDeleteButton';
import { ActivityRefBar } from './activity-reference-bar/ActivityRefBar';
import {
  getStoreyNumber,
  isStoreyRemovalBlockedByMezzanine,
} from '@shared/helpers/design/storey.helpers';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { StoreySettingsPanel } from './StoreySettingsPanel';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { PANEL_CONTENT_DEFAULT_SPACING } from '@ui/layout/panels/panel.constants';

type StoreyRowProps = {
  storey: Storey;
  storeyGfa: number;
  maxStoreyGfa: number;
  isSelected: boolean;
  isSettingsOpen: boolean;
  onSelect: (storeyId: Storey['id']) => void;
  onToggleSettings: (storeyId: Storey['id']) => void;
};
export const StoreyRow: React.FC<StoreyRowProps> = ({
  storey,
  storeyGfa,
  maxStoreyGfa,
  isSelected,
  isSettingsOpen,
  onSelect,
  onToggleSettings,
}) => {
  const { classes, cx } = useStyles();
  const removeStorey = useRemoveStorey();
  const [isHovered, setHoveredTrue, setHoveredFalse] = useBooleanState(false);
  const isReadonly = useIsReadonly();
  const storeys = usePanelStoreys();
  // Prevent removing the ground floor when a mezzanine relies on it.
  const isBlockedByMezzanineAbove = isStoreyRemovalBlockedByMezzanine(
    storeys,
    storey,
  );

  const deleteDisabledReason = isBlockedByMezzanineAbove
    ? 'Cannot delete the ground floor while a mezzanine sits above it.'
    : undefined;
  const isDeleteDisabled = Boolean(deleteDisabledReason);
  const storeyNumber = getStoreyNumber(storey, storeys);

  const handleSelect = useCallback(
    () => onSelect(storey.id),
    [onSelect, storey.id],
  );

  const handleRemove = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      if (isReadonly) return;
      event.stopPropagation();

      try {
        await removeStorey(storey.id);
      } catch (error: unknown) {
        if (isDesignSnapshotSupersededError(error)) return;
        throw error;
      }
      amplitudeLog(AMP_DESIGN_STOREY_DELETE, { StoreyId: storey.id });
    },
    [isReadonly, removeStorey, storey.id],
  );

  /**
   * Toggles the settings panel for the current storey.
   * @param event
   */
  const handleToggleSettings = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Prevent row selection from firing twice.
      event.stopPropagation();
      onToggleSettings(storey.id);
    },
    [onToggleSettings, storey.id],
  );

  return (
    <>
      <li
        className={cx(classes.listItem, isSelected && classes.rowSelected)}
        onClick={handleSelect}
        onMouseEnter={setHoveredTrue}
        onMouseLeave={setHoveredFalse}
      >
        <div className={classes.levelLabel}>
          <Tooltip title={deleteDisabledReason}>
            <HoverDeleteButton
              isHovered={isHovered}
              onDelete={handleRemove}
              disabled={isDeleteDisabled || isReadonly}
              aria-label="Remove storey"
            >
              <span className={classes.levelNumber}>
                <Typography variant="caption">{storeyNumber}</Typography>
              </span>
            </HoverDeleteButton>
          </Tooltip>
        </div>
        <div className={classes.barColumn}>
          <ActivityRefBar
            storey={storey}
            storeyGfa={storeyGfa}
            maxStoreyGfa={maxStoreyGfa}
            selected={isSelected}
          />
          <div className={classes.gfaCell}>
            <ExpressionTypography value={storeyGfa} unit="m²" variant="body2" />
          </div>
        </div>
        <div className={classes.settingsToggleCell}>
          <IconButton
            category="mui"
            icon="KeyboardArrowRight"
            rotateIcon={isSettingsOpen ? 90 : 0}
            shape="square"
            onClick={handleToggleSettings}
            disabled={isReadonly}
            aria-label={
              isSettingsOpen ? 'Hide storey settings' : 'Show storey settings'
            }
          />
        </div>
      </li>
      <StoreySettingsPanel storey={storey} open={isSettingsOpen} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  listItem: {
    display: 'grid',
    // Auto width so the negative margins reach both panel edges.
    alignItems: 'center',
    columnGap: theme.spacing(1.5),
    gridTemplateColumns: 'auto 1fr 40px',
    border: 'none',
    // Bleed the row background to the panel edges when selected.
    padding: theme.spacing(2, PANEL_CONTENT_DEFAULT_SPACING),
    margin: theme.spacing(0, -PANEL_CONTENT_DEFAULT_SPACING),
    textAlign: 'left',
    cursor: 'pointer',
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  rowSelected: {
    backgroundColor: 'rgba(0, 138, 235, 0.06)',
  },
  levelLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  levelNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  barColumn: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  gfaCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  settingsToggleCell: {
    display: 'flex',
    alignItems: 'center',
    justifySelf: 'end',
  },
}));
